import { useEffect, useRef, useState } from 'react';
import { useActiveContextOptional } from '@/auth/ActiveContextProvider';
import { useAccessEpoch } from '@/lib/access-epoch';
import { apiClient } from '@/lib/api-client';

/**
 * SL-0045 Activity 9 follow-up: reads `GET /v1/clinical-study/me/permissions` so a section whose
 * every write would 403 is not rendered at all.
 *
 * WHY THIS EXISTS AT ALL. `ClinicalStudyGate` records SL-0041 R6: the console cannot tell which of
 * the four module roles the caller holds, because `GET /v1/profile` returns `roleId` as a
 * per-environment UUID and core-api's permission-set resolution is trusted-service-only. The
 * consequence shipped as render-everything-and-let-the-server-deny, which was sound while every
 * user of this module was an administrator. Activity 9's `clinical-study:study:read` changed the
 * population: an investigator now reaches the study detail page and meets four administrator blocks
 * it cannot use. R6 rules out asking core-api from the browser; it does not rule out asking the
 * clinical-study module, which resolved the same set for its own guard on this very request.
 *
 * ADVISORY, NEVER AUTHORITATIVE. Hiding a control is a usability decision, not an access control:
 * the server re-checks every guarded route and remains the sole authority (plan Decision 10). That
 * is why the failure state below **hides** rather than reveals — see `canUse`.
 */
export type CallerPermissionsState =
  { status: 'loading' } | { status: 'ready'; permissions: readonly string[] } | { status: 'error' };

function missingOrganization(
  activeContext: ReturnType<typeof useActiveContextOptional>,
  organizationId: string | undefined,
): 'waiting' | 'none' | null {
  if (activeContext === undefined || organizationId !== undefined) return null;
  return activeContext.status === 'loading' ? 'waiting' : 'none';
}

export function useCallerPermissions(): CallerPermissionsState {
  const [state, setState] = useState<CallerPermissionsState>({ status: 'loading' });
  // The organization the state answers for. In the render where the organization changes, the
  // effect below has not yet re-entered `loading`, so the state still holds the previous answer:
  // reported as `loading` instead, so nothing (the start screen's wait, #430) takes it as current.
  const [answeredFor, setAnsweredFor] = useState<string | undefined>(undefined);
  // Permissions are held PER ORGANIZATION — the route resolves them from the active-context
  // headers and its own 403 says "in the active-context Organization". Before 2026-09-14 this
  // effect had an empty dependency list, so switching organization left the previous
  // organization's answer standing. That was survivable while only two pages used it to hide a
  // button; it is not survivable now that a navigation entry depends on it, because the rail would
  // keep advertising a module the new organization does not have.
  // Optional on purpose — see `useActiveContextOptional`. Where no provider is mounted the read
  // simply does not re-run, which is exactly right: nothing can tell it the organization changed.
  const activeContext = useActiveContextOptional();
  const organizationId = activeContext?.active?.organizationId;
  // A provider with no active organization: the read would go out without the
  // `X-Active-Organization-Id` header and answer 400. While the provider is still loading, one may
  // come, so the read waits. Once it has settled with none, none will: the answer is the refusal
  // that 400 gave, without the request — and never a `loading` that would hold the start screen.
  const withoutOrganization = missingOrganization(activeContext, organizationId);

  // Decision 151: a change of access re-reads; the organization already answered keeps its answer
  // on screen meanwhile, and keeps it if the re-read fails.
  const epoch = useAccessEpoch();
  const answeredOrg = useRef<string | undefined>(undefined);

  useEffect(() => {
    let active = true;
    const refresh = answeredOrg.current !== undefined && answeredOrg.current === organizationId;
    // Re-entering 'loading' on a switch is deliberate and matches the fail-closed rule below: the
    // previous organization's answer must stop counting the instant it stops applying.
    if (!refresh) {
      answeredOrg.current = undefined;
      setState({ status: 'loading' });
    }
    setAnsweredFor(organizationId);
    if (withoutOrganization === 'waiting') {
      return;
    }
    if (withoutOrganization === 'none') {
      setState({ status: 'error' });
      return;
    }
    apiClient
      .GET('/v1/clinical-study/me/permissions', {})
      .then(({ data, error, response }) => {
        if (!active) {
          return;
        }
        if (error !== undefined || !response.ok || data === undefined) {
          if (!refresh) {
            setState({ status: 'error' });
          }
          return;
        }
        answeredOrg.current = organizationId;
        setState({ status: 'ready', permissions: data.permissions });
      })
      .catch(() => {
        if (active && !refresh) {
          setState({ status: 'error' });
        }
      });
    // The `active` flag covers unmount AND supersession: a switch made while a request is in
    // flight abandons that request's result rather than letting the old organization's answer land
    // after the new one's.
    return () => {
      active = false;
    };
  }, [organizationId, withoutOrganization, epoch]);

  return answeredFor === organizationId ? state : { status: 'loading' };
}

/**
 * The single predicate every gated section uses.
 *
 * FAILS CLOSED, and that direction is the deliberate one. While loading, and on error, this returns
 * `false`, so a section is hidden until the caller is known to hold its permission. The mirror
 * choice — showing on error — would reproduce the exact defect this hook was added to remove
 * whenever the read fails, and it would do so invisibly, because the page would still look correct
 * to an administrator (for whom every section is legitimate). Hiding on error costs an
 * administrator a reload; showing on error costs an investigator the confusing page again.
 *
 * The cost is bounded by what this predicate is allowed to gate: it must never be the only thing
 * standing between a caller and an operation, because the server already is.
 */
export function canUse(state: CallerPermissionsState, permissionCode: string): boolean {
  return state.status === 'ready' && state.permissions.includes(permissionCode);
}
